import React, { FC, useEffect, useState } from "react"
import { SortParams, SummaryTrackRoyalty } from "../../../../types"
import { KInput, KSpan, KTooltip } from "kahuna-base-react-components"
import NotFound from "../../../NotFound"
import { lang } from "../../../../constants/languages"
import { convertToMonthYear, formatNumber } from "../../../../utility"
//@ts-ignore
import MoreIcon from "../../../../assets/admin_icons/horizontal-dots.svg"

type SummaryTrackRoyaltiesProps = {
  summaryTrackRoyalties: SummaryTrackRoyalty[]
  handleSort: (field: string) => void
  sort: SortParams
  selectedRowKeys: string[]
  onToggleRows: (rows: SummaryTrackRoyalty[], selected: boolean) => void
}

// A track is listed once per account, so the account is part of what identifies a row
export const getSummaryTrackRoyaltyKey = (row: SummaryTrackRoyalty) => `${row.userId}-${row.trackId}`

// Rows without a matched track or already in the open campaign can not be sent to Discovery Mode
export const isDiscoveryModeSelectable = (row: SummaryTrackRoyalty) =>
  !!row.userId && !!row.trackId && !row.discoveryModeSelected

// "202605-202606" -> "May 2026 - Jun 2026", "202609" -> "Sep 2026"
const formatPeriodRange = (range: string) =>
  range
    .split("-")
    .map((period) => convertToMonthYear(period))
    .join(" - ")

const SummaryTrackRoyalties: FC<SummaryTrackRoyaltiesProps> = (props) => {
  const { summaryTrackRoyalties, handleSort, sort, selectedRowKeys, onToggleRows } = props

  const selectableRows = summaryTrackRoyalties?.filter(isDiscoveryModeSelectable) || []
  const allSelected =
    selectableRows.length > 0 && selectableRows.every((row) => selectedRowKeys.includes(getSummaryTrackRoyaltyKey(row)))

  const renderCheckbox = (key: string, checked: boolean, disabled: boolean, onToggle: () => void) => {
    return (
      // KInput only passes `checked` through while it is true, so the key remounts it on every change
      <KInput
        key={`${key}-${checked}`}
        dataTestId={key}
        value={key}
        onChange={onToggle}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        // KInput switches to a white active background once it has a value, which a checkbox always has
        background="transparent"
        activeBackground="transparent"
        hoverBackground="transparent"
        shadowDisabled
        padding="0px"
        width={16}
        height={16}
        accentColor="black"
      />
    )
  }

  const renderSummaryTrackRoyalties = () => {
    const sortedSummaryTrackRoyalties = summaryTrackRoyalties?.sort((a, b) => {
      const sortKeyA = sort.field === "sum_stream" ? "stream" : "totalAmountUsd"
      const sortKeyB = sort.field === "sum_stream" ? "streamChange" : "royaltyChange"

      const currentA = a[sortKeyA] || 0
      const changeA = a[sortKeyB] || 0
      const previousA = currentA - changeA
      const percentageA = previousA !== 0 ? (changeA / previousA) * 100 : 0

      const currentB = b[sortKeyA] || 0
      const changeB = b[sortKeyB] || 0
      const previousB = currentB - changeB
      const percentageB = previousB !== 0 ? (changeB / previousB) * 100 : 0

      if (sort.field && sort.direction) {
        if (sort.direction === "asc") {
          return currentA - currentB
        } else {
          return currentB - currentA
        }
      } else {
        return Math.abs(percentageB) - Math.abs(percentageA)
      }
    })

    return sortedSummaryTrackRoyalties?.map((summaryTrackRoyalty) => {
      const background = "#00000008"
      const isPositiveAmount = summaryTrackRoyalty.royaltyChange > 0
      const isPositiveStream = summaryTrackRoyalty.streamChange > 0
      const isPositiveDiscoveryMode = summaryTrackRoyalty.discoveryModeFeeChange > 0
      const rowKey = getSummaryTrackRoyaltyKey(summaryTrackRoyalty)
      const selectable = isDiscoveryModeSelectable(summaryTrackRoyalty)
      // Oldest first, the last one is the still open range the track entered Discovery Mode with
      const discoveryModePeriods = summaryTrackRoyalty.discoveryModePeriods || []
      const discoveryModeEntryPeriod = discoveryModePeriods[discoveryModePeriods.length - 1]
      return (
        <div
          key={`${rowKey}-${summaryTrackRoyalty.isrc}`}
          className="flex p-2"
          style={{ width: "100%", background, borderRadius: "5px", borderBottom: "5px solid white" }}
        >
          <div style={{ width: "4%" }}>
            {renderCheckbox(
              `discovery-mode-row-${rowKey}`,
              !!summaryTrackRoyalty.discoveryModeSelected || selectedRowKeys.includes(rowKey),
              !selectable,
              () => onToggleRows([summaryTrackRoyalty], !selectedRowKeys.includes(rowKey))
            )}
          </div>
          <div style={{ width: "16%" }}>
            <div>
              <KTooltip
                position="bottom"
                content={<KSpan text={summaryTrackRoyalty?.user?.username || "-"} />}
                children={
                  <div
                    className="cursor-pointer"
                    onClick={() =>
                      window.open(
                        `${process.env.REACT_APP_FRONT_END_URL}/admin/user-asset?user_id=${summaryTrackRoyalty?.userId}`,
                        "_blank"
                      )
                    }
                  >
                    <KSpan
                      text={`${summaryTrackRoyalty?.user?.username?.substring(0, 14) || "-"}${
                        (summaryTrackRoyalty?.user?.username?.length || 0) > 14 ? "..." : ""
                      }`}
                    />
                  </div>
                }
              />
              <div
                className="cursor-pointer"
                onClick={() =>
                  window.open(
                    `${process.env.REACT_APP_FRONT_END_URL}/admin/catalog?isrc=${summaryTrackRoyalty?.isrc}`,
                    "_blank"
                  )
                }
              >
                <KSpan text={summaryTrackRoyalty?.isrc || "-"} />
              </div>
            </div>
          </div>
          <div style={{ width: "28%" }}>
            <div className="flex flex-col gap-1">
              <KSpan text={summaryTrackRoyalty.track?.title || "-"} />
              {summaryTrackRoyalty.discoveryModeSelected && (
                <div className="flex items-center flex-row gap-1">
                  <KSpan text="In Discovery Mode" fontSize={12} lineHeight="16px" color="#6B7280" />
                  {discoveryModeEntryPeriod && (
                    <div className="flex items-center flex-row gap-1">
                      <KSpan
                        text={`since ${convertToMonthYear(discoveryModeEntryPeriod)}`}
                        fontSize={12}
                        lineHeight="16px"
                        color="#6B7280"
                      />
                      {discoveryModePeriods.length > 1 && (
                        <KTooltip
                          position="left"
                          width={"auto"}
                          content={
                            <div className="flex flex-col gap-1 items-start justify-center w-max pr-1">
                              <KSpan text="Previous Discovery Mode periods" fontSize={12} fontWeight={500} />
                              {discoveryModePeriods.slice(0, -1).map((range, index: number) => {
                                return <KSpan key={index} text={`• ${formatPeriodRange(range)}`} fontSize={12} />
                              })}
                            </div>
                          }
                        >
                          <img src={MoreIcon} style={{ width: 14, height: 14, minWidth: 14, minHeight: 14 }} />
                        </KTooltip>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div style={{ width: "28%" }}>
            <KSpan text={summaryTrackRoyalty.track?.artistList?.map((artist) => artist.name)?.join(", ") || "-"} />
          </div>
          <div style={{ width: "12%" }}>
            <div className="flex flex-col gap-1">
              <KSpan text={formatNumber(summaryTrackRoyalty.stream || 0, 0)} />
              <div className="flex gap-1">
                <KSpan
                  text={`${isPositiveStream ? "+" : ""}${formatNumber(summaryTrackRoyalty.streamChange || 0, 1)}`}
                  color={isPositiveStream ? "#56E099" : "#FF5865"}
                />
                <KSpan
                  text={`(${(() => {
                    const current = summaryTrackRoyalty.stream || 0
                    const change = summaryTrackRoyalty.streamChange || 0
                    const previous = current - change
                    return previous !== 0 ? (change / previous) * 100 : 0
                  })().toFixed(0)}%)`}
                  color={isPositiveStream ? "#56E099" : "#FF5865"}
                />
              </div>
            </div>
          </div>
          <div style={{ width: "12%" }}>
            <div className="flex flex-col gap-1">
              <KSpan
                text={`${formatNumber(summaryTrackRoyalty.totalAmountUsd || 0, 1)} / ${formatNumber(
                  summaryTrackRoyalty.totalNetUsd || 0,
                  1
                )}`}
              />
              <div className="flex gap-1">
                <KSpan
                  text={`${isPositiveAmount ? "+" : ""}${formatNumber(summaryTrackRoyalty.royaltyChange || 0, 1)}`}
                  color={isPositiveAmount ? "#56E099" : "#FF5865"}
                />
                <KSpan
                  text={`(${(() => {
                    const current = summaryTrackRoyalty.totalAmountUsd || 0
                    const change = summaryTrackRoyalty.royaltyChange || 0
                    const previous = current - change
                    return previous !== 0 ? (change / previous) * 100 : 0
                  })().toFixed(0)}%)`}
                  color={isPositiveAmount ? "#56E099" : "#FF5865"}
                />
              </div>
            </div>
          </div>
          <div style={{ width: "9%" }}>
            <div className="flex flex-col gap-1">
              <KSpan text={`${formatNumber(summaryTrackRoyalty.discoveryModeFeeUsd || 0, 1)}`} />
              <div className="flex gap-1">
                <KSpan
                  text={`${isPositiveDiscoveryMode ? "+" : ""}${formatNumber(
                    summaryTrackRoyalty.discoveryModeFeeChange || 0,
                    1
                  )}`}
                  color={isPositiveDiscoveryMode ? "#56E099" : "#FF5865"}
                />
                <KSpan
                  text={`(${(() => {
                    const current = summaryTrackRoyalty.discoveryModeFeeUsd || 0
                    const change = summaryTrackRoyalty.discoveryModeFeeChange || 0
                    const previous = current - change
                    return previous !== 0 ? (change / previous) * 100 : 0
                  })().toFixed(0)}%)`}
                  color={isPositiveDiscoveryMode ? "#56E099" : "#FF5865"}
                />
              </div>
            </div>
          </div>
        </div>
      )
    })
  }

  return (
    <div className="w-full h-full">
      <div
        className="py-5 px-7 min-h-[100%]"
        style={{
          borderRadius: "10px",
          border: "1px solid var(--stroke-soft-200, #F3F3F3)",
          background: "var(--bg-white-0, #FFF)",
          boxShadow: "0px 2px 4px 0px rgba(27, 28, 29, 0.04)"
        }}
      >
        <div>
          <div className="flex mb-4 px-2" style={{ width: "100%" }}>
            <div style={{ width: "4%" }}>
              {renderCheckbox("discovery-mode-select-all", allSelected, selectableRows.length === 0, () =>
                onToggleRows(selectableRows, !allSelected)
              )}
            </div>
            <div style={{ width: "16%" }}>
              <KSpan color="#111" text={"User / Identifier"} />
            </div>
            <div style={{ width: "28%" }}>
              <KSpan color="#111" text={"Track Title"} />
            </div>
            <div style={{ width: "28%" }}>
              <KSpan color="#111" text={"Artist(s)"} />
            </div>
            <div
              className="cursor-pointer flex gap-1 items-center"
              style={{ width: "12%" }}
              onClick={() => handleSort("sum_stream")}
            >
              <KSpan color="#111" text={"Streams"} />
              {sort.field && sort.field === "sum_stream" && (
                <img
                  src={sort.direction === "asc" ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
                  alt="change-icon"
                  height={16}
                  width={16}
                />
              )}
            </div>
            <div
              className="cursor-pointer flex gap-1 items-center"
              style={{ width: "12%" }}
              onClick={() => handleSort("sum_amount_usd")}
            >
              <KSpan color="#111" text={"Gross / Net ($)"} />
              {sort.field && sort.field === "sum_amount_usd" && (
                <img
                  src={sort.direction === "asc" ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
                  alt="change-icon"
                  height={16}
                  width={16}
                />
              )}
            </div>
            <div
              className="cursor-pointer flex gap-1 items-center"
              style={{ width: "9%" }}
              onClick={() => handleSort("discovery_mode_fee_usd")}
            >
              <KTooltip
                width={"140px"}
                content={<KSpan text="Spotify Discovery Mode Fee" />}
                children={<KSpan text={`D.M. Fee ($)`} color="#111" />}
              />

              {sort.field && sort.field === "discovery_mode_fee_usd" && (
                <img
                  src={sort.direction === "asc" ? "/admin_icons/caret-up.svg" : "/admin_icons/caret-down.svg"}
                  alt="change-icon"
                  height={16}
                  width={16}
                />
              )}
            </div>
          </div>
          {summaryTrackRoyalties && !(summaryTrackRoyalties.length > 0) && (
            <div className="flex p-5 w-full justify-center mt-[100px]">
              <NotFound title={lang.not_found.trends.title} description={lang.not_found.trends.desc} imageWidth={400} />
            </div>
          )}
          {summaryTrackRoyalties && summaryTrackRoyalties.length > 0 && (
            <div className="flex p-2 mb-2" style={{ width: "100%", background: "#00000008", borderRadius: "5px" }}>
              <div style={{ width: "76%" }}>
                <KSpan fontWeight={600} color="#111" text={"TOTAL"} />
              </div>
              <div style={{ width: "12%" }}>
                <div className="flex flex-col gap-1">
                  <KSpan
                    text={formatNumber(summaryTrackRoyalties.reduce((sum, item) => sum + (item.stream || 0), 0))}
                  />
                  <KSpan
                    text={formatNumber(summaryTrackRoyalties.reduce((sum, item) => sum + (item.streamChange || 0), 0))}
                  />
                </div>
              </div>
              <div style={{ width: "12%" }}>
                <div className="flex flex-col gap-1">
                  <KSpan
                    text={`${formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.totalAmountUsd || 0), 0),
                      1
                    )} / ${formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.totalNetUsd || 0), 0),
                      1
                    )}`}
                  />
                  <KSpan
                    text={formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.royaltyChange || 0), 0),
                      1
                    )}
                  />
                </div>
              </div>
              <div style={{ width: "9%" }}>
                <div className="flex flex-col gap-1">
                  <KSpan
                    text={`${formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.discoveryModeFeeUsd || 0), 0),
                      1
                    )} `}
                  />
                  <KSpan
                    text={formatNumber(
                      summaryTrackRoyalties.reduce((sum, item) => sum + (item.discoveryModeFeeChange || 0), 0),
                      1
                    )}
                  />
                </div>
              </div>
            </div>
          )}
          {renderSummaryTrackRoyalties()}
        </div>
      </div>
    </div>
  )
}

export default SummaryTrackRoyalties
